import { Glyph } from '../chrome/Glyph.js';
import type { GlyphName } from '../chrome/glyph-paths.js';

/**
 * Weather, as a glyph plus its word.
 *
 * The glyph alone would be the only carrier of a state that decides whether a
 * mission can fly, so it never travels alone: the word is beside it in the
 * environment block and in the `title` on the compact region row.
 *
 * **`null` is unknown and draws as `?`.** Never as CLEAR, never as the current
 * value, and never as nothing at all — an absent row and a clear sky look the
 * same from across the room (must-not #3).
 */

const NOW_GLYPH: Readonly<Record<string, GlyphName>> = Object.freeze({
  CLEAR: 'weatherClear',
  RESTRICTIVE: 'weatherRestrictive',
  PROHIBITIVE: 'weatherProhibitive',
});

const NEXT_GLYPH: Readonly<Record<string, GlyphName>> = Object.freeze({
  CLEAR: 'weatherClearOutline',
  RESTRICTIVE: 'weatherRestrictiveOutline',
  PROHIBITIVE: 'weatherProhibitiveOutline',
});

export function weatherColour(state: string | null): string {
  switch (state) {
    case 'CLEAR':
      return 'var(--ok)';
    case 'RESTRICTIVE':
      return 'var(--warn)';
    case 'PROHIBITIVE':
      return 'var(--red-text)';
    default:
      return 'var(--text-disabled)';
  }
}

export function WeatherGlyph(props: {
  readonly state: string | null;
  /** The forecast draws hollow, the present draws filled — the design's FILL axis. */
  readonly outlook?: 'now' | 'next';
  readonly size?: number;
}): JSX.Element {
  const table = props.outlook === 'next' ? NEXT_GLYPH : NOW_GLYPH;
  const name = props.state === null ? undefined : table[props.state];
  const colour = weatherColour(props.state);

  if (name === undefined) {
    return (
      <span
        data-testid="weather-unknown"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: props.size ?? 17,
          height: props.size ?? 17,
          fontFamily: 'var(--font-data)',
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1,
          color: 'var(--text-disabled)',
        }}
      >
        ?
      </span>
    );
  }

  return (
    <span data-testid="weather-glyph" data-weather={props.state} style={{ color: colour }}>
      <Glyph name={name} size={props.size ?? 17} />
    </span>
  );
}
